/** Числа и слова для человека. Одно место — чтобы «—» везде значил одно и то же. */

/** Прочерк, а не ноль. Разница между «не измерено» и «ноль» — половина честности продукта. */
export const DASH = "—";

export function num(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return value.toLocaleString("ru-RU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function decimal(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return value.toLocaleString("ru-RU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function pct(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return `${value.toLocaleString("ru-RU", { maximumFractionDigits: digits })}%`;
}

export function signedPct(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toLocaleString("ru-RU", { maximumFractionDigits: digits })}%`;
}

export function money(value: number | null | undefined, currency = "RUB"): string {
  if (value === null || value === undefined) return DASH;
  const symbol = currency === "RUB" ? "₽" : currency === "USD" ? "$" : currency;
  return `${num(Math.round(value))} ${symbol}`;
}

export function date(value: string | null | undefined): string {
  if (!value) return DASH;
  return new Date(value).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}

export function dateTime(value: string | null | undefined): string {
  if (!value) return DASH;
  return new Date(value).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ago(value: string | null | undefined): string {
  if (!value) return "никогда";
  const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  return `${days} ${plural(days, "день", "дня", "дней")} назад`;
}

export function plural(count: number, one: string, few: string, many: string): string {
  if (count % 100 >= 11 && count % 100 <= 14) return many;
  const tail = count % 10;
  if (tail === 1) return one;
  if (tail >= 2 && tail <= 4) return few;
  return many;
}

export const CONFIDENCE_TITLE: Record<string, string> = {
  high: "уверенность высокая",
  medium: "уверенность средняя",
  low: "уверенность низкая",
  none: "не измерение",
};

export const STATEMENT_TITLE: Record<string, string> = {
  fact: "факт",
  observation: "наблюдение",
  hypothesis: "гипотеза",
};

export const AREA_TITLE: Record<string, string> = {
  content: "Контент",
  audience: "Аудитория",
  ads: "Закупы",
  competitors: "Конкуренты",
  automation: "Автопилот",
  health: "Здоровье",
};

export const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

// ── Даты и числа для графиков ───────────────────────────────────────────

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/** Дата из «2026-09-15» без сдвига часового пояса: без T00:00:00 браузер
 *  читает такую строку как UTC и в минусовых поясах показывает вчера. */
export function parseDay(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}

/** «15 сен» — для подписей оси. */
export function dayShort(value: string): string {
  const at = parseDay(value);
  return `${at.getDate()} ${MONTHS_SHORT[at.getMonth()]}`;
}

/** «15 сентября, вторник» — для подсказки, где место есть. */
export function dayFull(value: string): string {
  return parseDay(value).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    weekday: "short",
  });
}

/** «6,9 тыс.» — только для подписей оси, где места нет. В самих показателях
 *  число всегда полное: округлённый показатель — это уже не показатель. */
export function compact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} млн`;
  if (abs >= 10_000) return `${Math.round(value / 1000).toLocaleString("ru-RU")} тыс.`;
  if (abs >= 1_000) return `${(value / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} тыс.`;
  if (!Number.isInteger(value)) return decimal(value, abs < 10 ? 1 : 0);
  return num(value);
}

/** Знак у абсолютной разницы: «+128», «−307». Минус — типографский. */
export function signed(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  if (value === 0) return "0";
  const body = Math.abs(value).toLocaleString("ru-RU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${value > 0 ? "+" : "−"}${body}`;
}

/** Диапазон дат одной строкой: «12 июн — 16 сен». */
export function rangeLabel(from: string | null | undefined, to: string | null | undefined): string {
  if (!from || !to) return DASH;
  return `${dayShort(from)} — ${dayShort(to)}`;
}

/** Сколько знаков после запятой имеет смысл показывать.
 *
 *  «2 928,0 просмотра» — это ложная точность: десятых у просмотров не бывает,
 *  а нолик после запятой заставляет глаз читать лишний знак. Зато «6,8 реакции
 *  на 1000» без десятой превращается в «7» и теряет весь смысл. Поэтому
 *  разрядность зависит от величины числа, а не от вкуса автора страницы. */
export function auto(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  const abs = Math.abs(value);
  if (abs >= 100) return num(Math.round(value));
  if (abs >= 10) return decimal(value, 1);
  if (abs >= 1) return decimal(value, 1);
  return decimal(value, 2);
}

/** Служебные состояния по-русски.
 *
 *  «ok» и «failed» в таблице — это утечка внутренних названий наружу. Человек,
 *  который ведёт канал, не обязан знать, как называются наши поля. */
const STATE_WORD: Record<string, string> = {
  ok: "в порядке",
  error: "ошибка",
  failed: "не удалось",
  running: "идёт",
  pending: "в очереди",
  queued: "в очереди",
  skipped: "пропущено",
  idle: "ещё не запускался",
  done: "готово",
  new: "новый",
  stale: "устарело",
  unknown: "неизвестно",
};

export function stateWord(value: string | null | undefined): string {
  if (!value) return DASH;
  return STATE_WORD[value] ?? value;
}
